import React, { useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import Avatar from '@atlaskit/avatar';
import ProfileCardTrigger from '@atlaskit/profilecard/user';
import { N200, N40A } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { type ContentBodyItem } from '@atlassian/notification-types/types';

import { getUserAvatarFromARI } from '../../utils/avatar';
import { useProfileClient } from '../use-profile-client';

interface QuoteAuthorProps {
	author?: ContentBodyItem['author'];
	tall?: boolean;
	noFloat?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuoteAuthorWrapper = styled.span<{
	tall?: boolean;
	noFloat?: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		position: 'relative',
		width: '20px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: props.tall ? '1.65em' : '1.428em',
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(p) =>
		p.noFloat
			? ``
			: `
    float: left;
    margin-right: 4px;
    & > span.quote-avatar-positioner {
      display: inline-flex;
      align-items: center;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }
  `,
);

export const QuoteAuthor = ({
	author,
	tall,
	noFloat,
}: QuoteAuthorProps): React.ReactElement | null => {
	const avatarUrl = author && (author.avatarUrl ?? getUserAvatarFromARI(author.ari));
	const userId = useMemo(() => author?.ari?.split('/')[1], [author]);
	const profileClient = useProfileClient();

	const avatar = useMemo(
		() => (
			<Avatar
				name={author?.displayName ?? ''}
				appearance="circle"
				borderColor="transparent"
				src={avatarUrl ?? undefined}
				size="xsmall"
			/>
		),
		[author, avatarUrl],
	);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<QuoteAuthorWrapper noFloat={noFloat} tall={tall} className="quote-avatar">
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<span className="quote-avatar-positioner">
				{userId ? (
					<ProfileCardTrigger
						testId="quote-author-profile-card-trigger"
						trigger="hover"
						userId={userId}
						resourceClient={profileClient}
					>
						{avatar}
					</ProfileCardTrigger>
				) : (
					avatar
				)}
			</span>
		</QuoteAuthorWrapper>
	);
};

/**
 * Hard reliance on .ak-renderer-document to make quoted ADF grey.
 * This might break if AK renderer removes this className in the future
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const QuoteWrapper = styled.div({
	borderLeft: `2px solid ${token('color.border', N40A)}`,
	color: token('color.text.subtle', N200),
	paddingLeft: token('space.075', '6px'),
	marginBottom: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ak-renderer-document': {
		color: token('color.text.subtle', N200),
	},
	'&::after': {
		content: "' '",
		clear: 'both',
	},
});

/**
 * Line height comes from ADF renderer line height,
 * fixes a shift in text between expanded and collapsed.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DocumentWrapper = styled.div({
	lineHeight: '1.714em',
});
